import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

/**
 * Gestione centralizzata degli errori di validazione del backend.
 *
 * Il backend (ValidationPipe con exceptionFactory) risponde ai 400 di
 * validazione con:
 *   { statusCode: 400, code: "VALIDATION_ERROR",
 *     errors: [{ field: "email", code: "isEmail" }, ...] }
 *
 * `applyServerErrors(error, setError)` mappa ogni entry sul campo RHF
 * corrispondente: il messaggio localizzato appare sotto al campo
 * (i Field di CustomFormV3 rendono già `fieldState.error.message`).
 * I campi non presenti nel form o i code sconosciuti finiscono in un
 * toast: un errore non deve MAI essere invisibile.
 */

export interface ServerFieldError {
  field: string;
  code: string;
}

/** code class-validator → messaggio utente. Estendere qui, in un punto solo. */
export const SERVER_ERROR_MESSAGES: Record<string, string> = {
  isNotEmpty: "Campo obbligatorio",
  isDefined: "Campo obbligatorio",
  isEmail: "Email non valida",
  isString: "Valore non valido",
  isNumber: "Deve essere un numero",
  isInt: "Deve essere un numero intero",
  isBoolean: "Valore non valido",
  isEnum: "Valore non ammesso",
  isArray: "Valore non valido",
  isDate: "Data non valida",
  isDateString: "Data non valida",
  isMongoId: "Riferimento non valido",
  isPhoneNumber: "Numero di telefono non valido",
  minLength: "Valore troppo corto",
  maxLength: "Valore troppo lungo",
  min: "Valore troppo basso",
  max: "Valore troppo alto",
  isUrl: "URL non valido",
  whitelistValidation: "Campo non riconosciuto",
  // code issue zod — endpoint validati con i contract condivisi (@leopart/shared)
  too_small: "Troppo corto/obbligatorio",
  invalid_type: "Valore non valido",
  invalid_enum_value: "Valore non ammesso",
  too_big: "Troppo lungo",
};

const FALLBACK_MESSAGE = "Valore non valido";

export const messageForCode = (code: string): string =>
  SERVER_ERROR_MESSAGES[code] ?? FALLBACK_MESSAGE;

/** Estrae gli errori strutturati da un errore axios; null se non è un 400 di validazione. */
export function getServerValidationErrors(
  error: unknown
): ServerFieldError[] | null {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data;
  if (
    error.response?.status !== 400 ||
    data?.code !== "VALIDATION_ERROR" ||
    !Array.isArray(data?.errors)
  ) {
    return null;
  }
  return (data.errors as ServerFieldError[]).filter(
    (e) => typeof e?.field === "string" && typeof e?.code === "string"
  );
}

export interface ApplyServerErrorsOptions {
  /** Campi effettivamente presenti nel form: gli altri vanno a toast. Se omesso, tutti i field vengono passati a setError. */
  knownFields?: string[];
  /** Override della gestione errori non mappabili (default: toast.error). */
  onUnknown?: (errors: ServerFieldError[]) => void;
}

/**
 * Applica gli errori del backend al form RHF.
 * Ritorna true se l'errore era un 400 di validazione ed è stato gestito
 * (il chiamante non deve mostrare altri toast); false altrimenti.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  options: ApplyServerErrorsOptions = {}
): boolean {
  const errors = getServerValidationErrors(error);
  if (!errors || errors.length === 0) return false;

  const { knownFields, onUnknown } = options;
  const unknown: ServerFieldError[] = [];

  for (const serverError of errors) {
    if (knownFields && !knownFields.includes(serverError.field)) {
      unknown.push(serverError);
      continue;
    }
    setError(serverError.field as Path<T>, {
      type: "server",
      message: messageForCode(serverError.code),
    });
  }

  if (unknown.length > 0) {
    if (onUnknown) {
      onUnknown(unknown);
    } else {
      toast.error(
        `Dati non validi: ${unknown.map((e) => e.field).join(", ")}`
      );
    }
  }
  return true;
}

/**
 * Fallback per form SENZA react-hook-form (useSimpleForm, salvataggi bulk):
 * mostra i 400 di validazione come toast leggibile "campo: messaggio".
 * Ritorna true se l'errore era un 400 di validazione ed è stato mostrato.
 */
export function toastServerErrors(error: unknown): boolean {
  const errors = getServerValidationErrors(error);
  if (!errors || errors.length === 0) return false;
  const lines = errors
    .slice(0, 4)
    .map((e) => `${e.field}: ${messageForCode(e.code)}`);
  const extra = errors.length > 4 ? ` (+${errors.length - 4})` : "";
  toast.error(`Dati non validi — ${lines.join(" · ")}${extra}`, {
    duration: 6000,
  });
  return true;
}

/** Warning non bloccanti nel body di una risposta 2xx (es. EMAIL_SEND_FAILED). */
export const RESPONSE_WARNING_MESSAGES: Record<string, string> = {
  EMAIL_SEND_FAILED:
    "Utente creato, ma l'invio dell'email di conferma è fallito. Usa “Reinvia invito” dalla scheda utente.",
};

export function toastResponseWarning(data: unknown): boolean {
  const code = (data as { warning?: { code?: string } })?.warning?.code;
  if (!code) return false;
  toast.error(
    RESPONSE_WARNING_MESSAGES[code] ?? `Operazione completata con avviso: ${code}`,
    { duration: 8000 }
  );
  return true;
}
